import { Box, Spinner, Text } from '@nimbus-ds/components';
import classNames from 'classnames';
import { CardWithTitle } from 'commons/components';
import { AiIcon } from 'commons/components/GenerateWithAiLink/AiIcon';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  AlertMaxImages,
  MediaDropArea,
  MediaPreview,
  RecommendationsGridRow,
  UploadingMessage,
} from 'domains/Catalog/Products/pages/components/ImageGallery/components';
import { useImageGallery } from 'domains/Catalog/Products/pages/components/ImageGallery/hooks';
import { ImageGalleryState } from 'domains/Catalog/Products/pages/components/ImageGallery/ImageGallery';
import { MAX_IMAGES_TO_LOAD } from 'domains/Catalog/Products/pages/NewProductFromImagesPage/constants';

import './ImageGalleryGrid.scss';

interface ImageGalleryGridProps {
  onChange: (images: ImageGalleryState[], id?: string) => void;
  images: ImageGalleryState[];
  isIdentifying: boolean;
}

function ImageGalleryGrid({
  onChange,
  images,
  isIdentifying,
}: Readonly<ImageGalleryGridProps>): JSX.Element {
  const t = useTranslationCatalog();

  const {
    message,
    videoQuantity,
    handleOnchange,
    handleOnRemove,
    handleOnClickAdd,
    onRetryError,
  } = useImageGallery({
    images,
    isEdit: false,
    onChange,
  });

  return (
    <div
      className={classNames('stratus--image-gallery-grid', {
        'pulse-animation': isIdentifying,
      })}
      aria-busy={isIdentifying}
    >
      <CardWithTitle title={t('products.modal.photos')} type="h4">
        <Box display="flex" flexDirection="column" gap="4">
          <AlertMaxImages
            maxImagesToLoad={MAX_IMAGES_TO_LOAD}
            images={images}
          />
          <MediaDropArea
            isEdit={false}
            videoQuantity={videoQuantity}
            onUploadImages={handleOnchange}
            onClick={handleOnClickAdd}
            allowUploadVideos={false}
          />
          <MediaPreview
            images={images}
            showFirstImageAsMain={false}
            onRemove={handleOnRemove}
            onError={onRetryError}
          />
          <RecommendationsGridRow allowUploadVideos={false} />
          {!!message && <UploadingMessage message={message} />}
        </Box>
      </CardWithTitle>
      {isIdentifying && (
        <div
          className="blocking-overlay"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-labelledby="identifying-label"
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="4"
          >
            <Spinner />
            <Box display="flex" alignItems="center" gap="2">
              <AiIcon />
              <Text id="identifying-label">
                {t('products.fromImages.identifying')}
              </Text>
            </Box>
          </Box>
        </div>
      )}
    </div>
  );
}

export default ImageGalleryGrid;
